import { iconSizes } from "./theme.mjs";
import { Text, TextStylesProvider } from "./Text.mjs";
import { useTheme } from "./ThemeProvider.mjs";
import { Flex } from "./Flex.mjs";
import { useStyles } from "purse-styles";
import React from "react";
import { match } from "@repo/utils/utils";
import { Button } from "react-aria-components";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/SocialButton.tsx
const GoogleIcon = ({ size = "md" }) => {
	return /* @__PURE__ */ jsxs("svg", {
		width: iconSizes[size],
		height: iconSizes[size],
		viewBox: "0 0 24 24",
		fill: "none",
		style: {
			pointerEvents: "none",
			flexShrink: 0
		},
		children: [
			/* @__PURE__ */ jsx("path", {
				d: "M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z",
				fill: "#4285F4"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.2444 19.252C9.11376 19.252 6.45934 17.1399 5.50693 14.3003H1.51648V17.3912C3.55359 21.4434 7.70278 24.0008 12.24 24.0008Z",
				fill: "#34A853"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z",
				fill: "#FBBC04"
			}),
			/* @__PURE__ */ jsx("path", {
				d: "M12.24 4.74966C13.9508 4.7232 15.6043 5.36697 16.8433 6.54867L20.2694 3.12262C18.1 1.0855 15.2207 -0.034466 12.24 0.000808666C7.70277 0.000808666 3.55359 2.55822 1.51648 6.61481L5.50252 9.70575C6.45052 6.86173 9.10935 4.74966 12.24 4.74966Z",
				fill: "#EA4335"
			})
		]
	});
};
const MicrosoftIcon = ({ size = "md" }) => {
	return /* @__PURE__ */ jsxs("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		viewBox: "0 0 23 23",
		width: iconSizes[size],
		height: iconSizes[size],
		style: {
			pointerEvents: "none",
			flexShrink: 0
		},
		children: [
			/* @__PURE__ */ jsx("path", {
				fill: "#f35325",
				d: "M1 1h10v10H1z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#81bc06",
				d: "M12 1h10v10H12z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#05a6f0",
				d: "M1 12h10v10H1z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#ffba08",
				d: "M12 12h10v10H12z"
			})
		]
	});
};
const socialProviders = {
	google: {
		Icon: GoogleIcon,
		backgroundColor: "#4285f4",
		textColor: "#3C4043"
	},
	microsoft: {
		Icon: MicrosoftIcon,
		backgroundColor: "#ffffff",
		textColor: "#727272"
	}
};
const SocialButton = ({ provider, children, style: style$1, size = "md", variant = "outline", onPress, disabled = false }) => {
	const theme = useTheme();
	const providerInfo = socialProviders[provider];
	const variantStyles = match(variant, {
		primary: () => {
			return {
				background: providerInfo.backgroundColor,
				border: `1px solid ${providerInfo.backgroundColor}`,
				"&:hover": { opacity: .9 },
				"&[data-pressed]": { opacity: .8 }
			};
		},
		outline: () => {
			return {
				background: theme.colors.sand1,
				border: `1px solid ${theme.colors.sandA5}`,
				color: theme.colors.sand12,
				"&:hover": {
					background: theme.colors.sand3,
					borderColor: theme.colors.sandA8
				},
				"&[data-pressed]": { background: theme.colors.sand4 }
			};
		}
	});
	const className = useStyles({
		opacity: disabled ? .5 : 1,
		cursor: disabled ? "not-allowed" : "pointer",
		overflow: "hidden",
		...variantStyles,
		...style$1
	});
	const content = children || `Continue with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`;
	return /* @__PURE__ */ jsx(Button, {
		onPress: disabled ? void 0 : onPress,
		isDisabled: disabled,
		children: /* @__PURE__ */ jsxs(Flex, {
			className,
			align: "center",
			justify: "center",
			gap: "sm",
			padding: "sm",
			borderRadius: "md",
			children: [/* @__PURE__ */ jsx(providerInfo.Icon, { size }), /* @__PURE__ */ jsx(TextStylesProvider, {
				weight: "medium",
				capsize: true,
				size,
				children: typeof content === "string" ? match(variant, {
					primary: () => {
						return /* @__PURE__ */ jsx(Text, {
							style: { color: providerInfo.textColor },
							children: content
						});
					},
					outline: () => {
						return /* @__PURE__ */ jsx(Text, { children: content });
					}
				}) : content
			})]
		})
	});
};

//#endregion
export { SocialButton };